import type { UseFormReturn } from 'react-hook-form';
import { Info } from 'lucide-react';
import type { SipPeerFormData } from '../schema';

/** General tab: name, type, domain, description, enabled. */
export function GeneralTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure the basic connection settings for your SIP peer.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Peer Name *
          </label>
          <input
            {...form.register('name')}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Carrier-Primary"
          />
          {form.formState.errors.name && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Peer Type
          </label>
          <select
            {...form.register('type')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="carrier">PSTN Carrier</option>
            <option value="teams">Microsoft Teams</option>
            <option value="webrtc">WebRTC</option>
            <option value="softphone">SIP Softphone</option>
            <option value="pbx">PBX System</option>
          </select>
          {form.watch('type') === 'teams' && (
            <p className="mt-2 text-xs text-blue-600 flex items-center">
              <Info className="w-3 h-3 mr-1" />
              AudioCodes SBC emulation enabled for Teams compatibility
            </p>
          )}
        </div>

        {/* Domain - required for carriers/teams/pbx, optional for softphones/webrtc */}
        {(() => {
          const peerType = form.watch('type');
          const requiresDomain = ['carrier', 'teams', 'pbx'].includes(peerType);
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Domain {requiresDomain && '*'}
                {!requiresDomain && <span className="text-gray-400 font-normal">(optional)</span>}
              </label>
              <input
                {...form.register('domain')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={requiresDomain ? "e.g., sip.carrier.com" : "Leave empty if clients register to SBC domain"}
                onChange={(e) => {
                  form.setValue('domain', e.target.value);
                  // Auto-update SIP URI if domain is provided
                  if (e.target.value) {
                    const currentUri = form.getValues('sipUri');
                    const uriMatch = currentUri?.match(/^sip:([^@]*)@/);
                    const userPart = uriMatch ? uriMatch[1] : 'peer';
                    form.setValue('sipUri', `sip:${userPart}@${e.target.value}`);
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                {requiresDomain
                  ? "The SIP domain of this peer (used for routing and registration matching)."
                  : "Used to identify this peer when source IP doesn't match (e.g. REGISTER). At least one of domain or source IP ranges is required."}
              </p>
              {form.formState.errors.domain && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.domain.message}</p>
              )}
            </div>
          );
        })()}

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            {...form.register('metadata.description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Optional description for this peer connection"
          />
        </div>

        <div className="flex items-center">
          <input
            {...form.register('enabled')}
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            Enable this peer connection
          </label>
        </div>
      </div>
    </div>
  );
}
